import { IconButton, SkeletonLine, TableRow } from '@one-thd/sui-atomic-components';
import { ArrowDown, ArrowUp } from '@one-thd/sui-icons';
import get from 'lodash.get';
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { DataGridCell } from './DataGridCell';
import { DataGridData, DataGridInnerRowsProps } from './types';

// Hack around propTypes breaking generic interfaces
type DataGridInnerRowsComponent = {
  <TData extends DataGridData>(props: DataGridInnerRowsProps<TData>): React.JSX.Element;
  // eslint-disable-next-line @typescript-eslint/ban-types
  propTypes?: {};
  displayName: string;
};

/**
 * DataGridInnerRows component renders the rows of a data grid, including skeleton rows when loading.
 * It supports expandable rows, custom row and cell rendering, and row click handling.
 *
 * @template TData - The type of data used in the data grid.
 *
 * @param {DataGridInnerRowsProps<TData>} props - The properties for the DataGridInnerRows component.
 * @param {TData[]} props.data - The data to be displayed in the grid.
 * @param {Array} props.columns - The columns configuration for the grid.
 * @param {Object} [props.expandable] - Configuration for expandable rows.
 * @param {Function} [props.expandable.isExpanded] - Function to determine if a row is expanded.
 * @param {Function} [props.expandable.isExpandable] - Function to determine if a row is expandable.
 * @param {Function} [props.getRowProps] - Function to get additional props for a row.
 * @param {Function} [props.onRowClick] - Function to handle row click events.
 * @param {string | Function} props.dataId - The identifier for the data rows.
 * @param {boolean} [props.loading] - Flag indicating if the grid is in a loading state.
 * @param {number} [props.skeletonRowCount=1] - The number of skeleton rows to display when loading.
 * @param {Function} [props.renderRowChildren] - Function to render additional content for each row.
 * @param {Array} [props.checkedRows] - Array of checked row identifiers.
 * @param {React.ComponentType} [props.SkeletonComponent] - Custom component to render skeleton rows.
 * @param {React.ComponentType} [props.RowComponent] - Custom component to render rows.
 * @param {Function} [props.onRowCheck] - Function to handle row check events.
 *
 * @returns {React.JSX.Element} The rendered DataGridInnerRows component.
 */
export const DataGridInnerRows: DataGridInnerRowsComponent = <
  TData extends DataGridData,
>(
    props: DataGridInnerRowsProps<TData>
  ) => {
  const {
    data,
    columns,
    expandable,
    getRowProps,
    onRowClick,
    dataId,
    loading,
    skeletonRowCount = 1,
    renderRowChildren,
    checkedRows,
    SkeletonComponent,
    RowComponent,
    onRowCheck,
    getRowCellProps
  } = props;

  const handleRowClick = useCallback((item: TData) => {
    if (!onRowClick) return;

    return (event: React.MouseEvent) => {
      onRowClick?.(event, item);
    };
  }, []);

  const Skeleton = SkeletonComponent || (() => (
    <SkeletonLine
      variant="single"
      // @ts-ignore: missing className in generated types
      className="sui-w-full"
    />
  ));

  const Row = RowComponent || TableRow;

  return (
    <>
      {loading
        ? Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
          <Row key={`skeleton-row-${rowIndex}`} data-testid="skeleton-row">
            {
              !!expandable && (
                <DataGridCell
                  data-testid="skeleton-cell"
                >
                  <Skeleton />
                </DataGridCell>
              )
            }
            {columns.map((column, cellIndex) => {
              const { colSpan, renderSkeleton } = column;
              return (
                <DataGridCell
                  key={`skeleton-cell-${rowIndex}-${cellIndex}`}
                  data-testid="skeleton-cell"
                  colSpan={colSpan}
                >
                  {renderSkeleton ? renderSkeleton(column) : (
                    <Skeleton />
                  )}

                </DataGridCell>
              );
            })}
          </Row>
        ))
        : data.map((item, index) => {
          const rowKey = typeof dataId === 'function' ? dataId(item, index) : get(item, dataId);

          const expanded = expandable?.isExpanded?.(item);
          // If not "isExpandable" function is passed, then all rows are expandable
          const isExpandable = expandable?.isExpandable?.(item);
          const hasExpandableRows = !!expandable;

          return (
            <React.Fragment key={`${rowKey}`}>
              <Row
                {...getRowProps?.(item)}
                onClick={handleRowClick(item)}
              >

                {isExpandable && (
                  <DataGridCell>
                    <IconButton
                      buttonSize="tight"
                      aria-label="expand row"
                      icon={expanded ? ArrowUp : ArrowDown}
                    />
                  </DataGridCell>
                )}
                {((!isExpandable && hasExpandableRows)) && <DataGridCell />}
                {columns.map(
                  (column) => {
                    const {
                      renderCell, key, dataIndex, colSpan, align, sticky
                    } = column;
                    return (
                      <React.Fragment key={`${rowKey}-${key}`}>
                        {renderCell && renderCell({
                          item,
                          column,
                          onRowCheck: (e) => onRowCheck?.(e, rowKey),
                          checked: checkedRows?.includes(rowKey)
                        })}
                        {dataIndex && (
                          <DataGridCell
                            colSpan={colSpan}
                            align={align}
                            sticky={sticky ? 'horizontal' : undefined}
                            {...getRowCellProps?.(item, column)}
                          >
                            {get(item, dataIndex)}
                          </DataGridCell>
                        )}
                      </React.Fragment>
                    );
                  }
                )}
              </Row>
              {renderRowChildren?.(item)}
            </React.Fragment>
          );
        })}
    </>
  );
};

DataGridInnerRows.propTypes = {
  expandable: PropTypes.shape({
    isExpanded: PropTypes.func,
    renderExpandableItem: PropTypes.func,
    isExpandable: PropTypes.func
  }),
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    colSpan: PropTypes.number,
    align: PropTypes.oneOf(['left', 'right', 'center', 'justify']),
    enableSorting: PropTypes.bool,
    isNumber: PropTypes.bool,
    isCheckable: PropTypes.bool,
    renderCell: PropTypes.func,
    dataIndex: PropTypes.string,
    sticky: PropTypes.bool
  })).isRequired,
  skeletonRowCount: PropTypes.number,
  onRowCheck: PropTypes.func,
  onRowClick: PropTypes.func,
  getRowProps: PropTypes.func,
  getRowCellProps: PropTypes.func,
  loading: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dataId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]).isRequired,
// Typescript overrides types for props when types include a generic interface and propTypes are passed
// eslint-disable-next-line @typescript-eslint/ban-types
} as {};

DataGridInnerRows.displayName = 'DataGridInnerRows';
